import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  HeadingJumbo,
  HeadingLarge,
  HeadingMedium,
  HeadingSmall,
  HeadingHeroBanner,
  HeadingLargeUppercase,
  Prefix,
  Link,
  linkStyles,
  PageHeading,
  SubtitleHeading,
  HeadingXSmall,
  HeadingMediumSackers,
  HeadingLargeSackers
} from '@ycos/primitives'
import { Picture } from '@ycos/picture'
import { BlockVideo } from '@ycos/component-video'
import { createComponent, withTheme } from '@ycos/fela'
import { hasTag } from '@ycos/coremedia-renderer'
import Markdown from '@ycos/markdown'
import CallToAction from '@ycos/component-cta'
import replaceThreshold from './replaceThreshold'

const LOYALTY_LARGE_BUTTON = '334px'

const WrappedLinkHOC = ({ target, onTargetClick, isFullCTA, additionalOnClick }) => ({ children, ...rest }) => {
  if (!isFullCTA && target) {
    const handleClick = ({ additionalOnClick, onTargetClick }) => {
      additionalOnClick && additionalOnClick()
      onTargetClick && onTargetClick()
    }

    return (
      <Link
        data-testid="wrappedlinkblock"
        data-seo-segment={target.seoSegment}
        href={target.href}
        onClick={() => handleClick({ onTargetClick, additionalOnClick })}
        {...rest}
      >
        {children}
      </Link>
    )
  }
  return children
}

const BlockAnchor = createComponent(
  'BlockAnchor',
  ({ theme, inverseColor }) => ({
    ':hover [data-automation="block-cta"]': {
      ...linkStyles({ theme, inverseColor })[':hover']
    }
  }),
  'a'
)

const BlockWrapperHOC = ({ target, onTargetClick, isFullCTA, inverseColor, campaignTestBlock, additionalOnClick }) => ({ children }) => {
  const handleClick = ({ additionalOnClick, onTargetClick }) => {
    additionalOnClick && additionalOnClick()
    onTargetClick && onTargetClick()
  }

  if (isFullCTA && target && !campaignTestBlock) {
    return (
      <BlockAnchor inverseColor={inverseColor} href={target.href} onClick={() => handleClick({ additionalOnClick, onTargetClick })}>
        {children}
      </BlockAnchor>
    )
  }
  if (isFullCTA && target && campaignTestBlock) {
    return (
      <BlockAnchor inverseColor={inverseColor} href={target.href} onClick={() => handleClick({ additionalOnClick, onTargetClick })}>
        {children}
      </BlockAnchor>
    )
  }
  return children
}

const MediaContainer = createComponent(
  'MediaContainer',
  ({ theme, mediaOnly, isLoyaltyStyle }) => {
    const loyaltyStyleDesktop = isLoyaltyStyle ? `${4 * theme.spacingMultiplier}px` : `${2 * theme.spacingMultiplier}px`
    return {
      marginBottom: isLoyaltyStyle ? `${3 * theme.spacingMultiplier}px` : `${2 * theme.spacingMultiplier}px`,
      'screen-medium': {
        marginBottom: mediaOnly ? 0 : loyaltyStyleDesktop
      }
    }
  },
  'div'
)

const ButtonWrapper = createComponent(
  'ButtonWrapper',
  ({ theme, isLoyaltyStyle }) => {
    const extraLargeButtonStyle = isLoyaltyStyle
      ? {
        maxWidth: '100%',
        height: '48px',
        'screen-medium': {
          width: LOYALTY_LARGE_BUTTON,
          height: '40px'
        }
      }
      : {}
    // add this style to make sure remove the parent wrapper max button width 326px
    const removeButtonParentWidthLimit = isLoyaltyStyle
      ? {
        '& div': {
          width: '100%',
          'screen-medium': {
            width: 'auto'
          }
        }
      }
      : {}

    return {
      display: 'flex',
      justifyContent: 'center',
      paddingTop: isLoyaltyStyle ? `${theme.spacingMultiplier}px` : `${2 * theme.spacingMultiplier}px`,
      'screen-medium': {
        paddingTop: isLoyaltyStyle ? `${theme.spacingMultiplier}px` : `${4 * theme.spacingMultiplier}px`
      },
      '& button': {
        padding: `0 ${2 * theme.spacingMultiplier}px`,
        ...extraLargeButtonStyle
      },
      ...removeButtonParentWidthLimit
    }
  },
  'div'
)

export const TextAlignment = (props, tags) => {
  const centerTextAlignment = hasTag(tags, 'Hero.centerTextAlignment') || hasTag(tags, 'Block.centerTextAlignment') || props.centerTextAlignment
  const rightTextAlignment = hasTag(tags, 'Block.rightTextAlignment') || props.rightTextAlignment
  if (centerTextAlignment) {
    return 'center'
  } else if (rightTextAlignment) {
    return 'right'
  }
  return 'left'
}

const TargetWrapper = createComponent('TargetWrapper', ({ textAlign = 'left' }) => ({
  textAlign
}))

const TeaserWrapper = createComponent('TeaserWrapper', ({ isLoyaltyStyle, theme }) => ({
  '& p': isLoyaltyStyle ? theme.custom.heading?.paragraphFontStyle : {}
}))

const CtaButton = ({ href, teaserTitle, ctaStyle, onTargetClick, isLoyaltyStyle, brand = {} }) => {
  return (
    <ButtonWrapper isLoyaltyStyle={isLoyaltyStyle}>
      <CallToAction
        item={{
          layoutVariant: `isc-cta-${ctaStyle}`,
          href,
          teaserTitle
        }}
        brand={brand.brandId}
        onTargetClick={onTargetClick}
      />
    </ButtonWrapper>
  )
}

const BlockLink = createComponent('BlockLink', linkStyles, 'span')

const RenderPicture = ({ fetchPriority, images, imagesphere, imageProps, isPictureFullWidth }) => {
  return isPictureFullWidth ? (
    <Picture fetchPriority={fetchPriority} images={images} imagesphere={imagesphere} {...imageProps} widths={[1500, 3000]} />
  ) : (
    <Picture fetchPriority={fetchPriority} images={images} imagesphere={imagesphere} {...imageProps} />
  )
}

const useThresholdData = () => {
  const [thresholdData, setThresholdData] = useState(null)

  useEffect(() => {
    const fetchThresholdData = async () => {
      try {
        const result = await window?.SF?.getTierThresholds()
        setThresholdData(result)
      } catch (error) {
        console.error('Error fetching threshold data:', error)
      }
    }

    fetchThresholdData()
  }, [])

  return { thresholdData }
}

const Block = (props) => {
  const [isMobile, setIsMobile] = useState(false)

  const handleMediaQueryChange = (mediaQueryList) => {
    setIsMobile(mediaQueryList.matches)
  }

  useEffect(() => {
    const mediaQueryList = window.matchMedia(`(max-width: ${props.theme.mediaQuery.maxPx.small}px)`)
    handleMediaQueryChange(mediaQueryList)
    mediaQueryList.addListener(handleMediaQueryChange)
    return () => {
      mediaQueryList.removeListener(handleMediaQueryChange)
    }
  })

  const { onTargetClick, config, onMarkdownTargetClick, imageProps, removeLastChildMargin, brand, additionalOnClick } = props

  const {
    teaserText,
    preTitlePlain,
    subTitlePlain,
    teaserTitle,
    layoutVariant,
    pictures = [],
    videos = [],
    target,
    tags = '',
    anchorpoint,
    dataCmMetadata,
    ynapParameter,
    fetchPriority
  } = props.item
  const mediaOnly =
    typeof teaserText === 'undefined' &&
    typeof preTitlePlain === 'undefined' &&
    typeof subTitlePlain === 'undefined' &&
    typeof teaserTitle === 'undefined' &&
    (typeof target === 'undefined' || (target && typeof target.teaserTitle === 'undefined'))
  const inverseColor = hasTag(tags, 'Colors.Inverse') || props.inverseColor
  // This is temprate for AB test https://jira.nap/browse/WEBCON-856
  const campaignTestBlock = hasTag(tags, 'ABTest.Campaign')
  const dropCaps = hasTag(tags, 'Paragraph.DropCaps') || props.dropCaps
  const isFullCTA = hasTag(tags, 'Block.FullCTA') || props.fullCTA
  // because removeLastChildMargin default vlaue is true,we need make sure when item have tag set the removeLastChildMargin to false
  const isAddExtraCtaMarginTop = hasTag(tags, 'Block.addExtraCtaMarginTop') ? false : removeLastChildMargin
  const isLinkPrimaryCTAButton = hasTag(tags, 'Block.PrimaryButtonCTA') || props.isLinkPrimaryCTAButton
  const isLinkSecondaryCTAButton = hasTag(tags, 'Block.SecondaryButtonCTA') || props.isLinkSecondaryCTAButton
  const isLoyaltyStyle = hasTag(tags, 'Block.LoyaltyStyle') || props.isLoyaltyStyle
  const isWithLoyaltyThreshold = hasTag(tags, 'Block.withLoyaltyThreshold') || props.isWithLoyaltyThreshold

  let ctaStyle
  ctaStyle = isLinkPrimaryCTAButton ? 'primary' : null
  ctaStyle = isLinkSecondaryCTAButton ? 'secondary' : ctaStyle
  const textAlign = TextAlignment(props, tags)
  const HeadingComponent =
    {
      'isc-block-with-jumbo-title': HeadingJumbo,
      'isc-block-with-large-uppercase-title': HeadingLargeUppercase,
      'isc-block-with-xlarge-title': PageHeading,
      'isc-block-with-large-title': HeadingLarge,
      'isc-block-with-large-title-sackers': HeadingLargeSackers,
      'isc-block-with-medium-title': HeadingMedium,
      'isc-block-with-medium-title-sackers': HeadingMediumSackers,
      'isc-block-with-small-title': HeadingSmall,
      'isc-block-with-xsmall-title': HeadingXSmall,
      'isc-block': HeadingSmall,
      'isc-block-hero': HeadingHeroBanner
    }[layoutVariant] || HeadingSmall

  const hasMedia = !!(videos.length || pictures.length)
  const video = videos[0]
  const hasVideo = video || ynapParameter
  const allowMuteOnVideo = hasTag(tags, 'Video.allowMute')

  const isPictureFullWidth = hasTag(tags, 'Block.fullWidth')

  const WrappedLink = WrappedLinkHOC({ target, onTargetClick, isFullCTA, additionalOnClick })
  const BlockWrapper = BlockWrapperHOC({ target, onTargetClick, isFullCTA, campaignTestBlock, additionalOnClick })
  const BlockCustomLink = isFullCTA ? BlockLink : WrappedLink
  const { thresholdData } = useThresholdData()
  // For the loyalty tier expansion, we need to automatically replace the correct values from the threshold API.
  // To avoid adding loyaltyNewNextTierThreshold to every isc-block in the Editorial APP, we should only apply it to isc-blocks with the tag block.withLoyaltyThreshold.
  const finalTeaserText = thresholdData && teaserText && isWithLoyaltyThreshold ? replaceThreshold(teaserText, thresholdData) : teaserText

  return (
    <div data-cm-metadata={dataCmMetadata}>
      <BlockWrapper inverseColor={inverseColor}>
        {hasMedia && (
          <WrappedLink>
            <MediaContainer mediaOnly={mediaOnly} isLoyaltyStyle={isLoyaltyStyle}>
              {hasVideo ? (
                <BlockVideo
                  ynapParameter={ynapParameter}
                  config={config}
                  pictures={pictures}
                  imageProps={imageProps}
                  isMobile={isMobile}
                  video={video}
                  allowMute={allowMuteOnVideo}
                  isPictureFullWidth={isPictureFullWidth}
                />
              ) : (
                <RenderPicture
                  fetchPriority={fetchPriority}
                  images={pictures}
                  imagesphere={config.imageSphere}
                  imageProps={imageProps}
                  isPictureFullWidth={isPictureFullWidth}
                />
              )}
            </MediaContainer>
          </WrappedLink>
        )}
        {subTitlePlain && <SubtitleHeading textAlign={textAlign}>{subTitlePlain}</SubtitleHeading>}
        {preTitlePlain && (
          <Prefix textAlign={textAlign} inverseColor={inverseColor}>
            {preTitlePlain}
          </Prefix>
        )}
        {teaserTitle && (
          <WrappedLink>
            <HeadingComponent textAlign={textAlign} inverseColor={inverseColor} isLoyaltyStyle={isLoyaltyStyle}>
              {teaserTitle}
            </HeadingComponent>
          </WrappedLink>
        )}
        {teaserText && (
          <TeaserWrapper isLoyaltyStyle={isLoyaltyStyle}>
            <Markdown
              anchorpoint={anchorpoint}
              textAlign={textAlign}
              teaserText={finalTeaserText}
              inverseColor={inverseColor}
              dropCaps={dropCaps}
              onMarkdownTargetClick={onMarkdownTargetClick}
              removeLastChildMargin={isAddExtraCtaMarginTop}
              smallMargin={target?.teaserTitle}
            />
          </TeaserWrapper>
        )}
        {target?.teaserTitle &&
          (ctaStyle ? (
            <CtaButton
              href={target.href}
              teaserTitle={target.teaserTitle}
              ctaStyle={ctaStyle}
              onTargetClick={onTargetClick}
              brand={brand}
              isLoyaltyStyle={isLoyaltyStyle}
            />
          ) : (
            <TargetWrapper textAlign={textAlign}>
              <BlockCustomLink inverseColor={inverseColor} data-automation="block-cta">
                {target.teaserTitle}
              </BlockCustomLink>
            </TargetWrapper>
          ))}
      </BlockWrapper>
    </div>
  )
}

Block.layoutVariant = [
  'isc-block',
  'isc-block-hero',
  'isc-block-with-xlarge-title',
  'isc-block-with-large-title',
  'isc-block-with-large-title-sackers',
  'isc-block-with-medium-title',
  'isc-block-with-medium-title-sackers',
  'isc-block-with-small-title',
  'isc-block-with-large-uppercase-title',
  'isc-block-with-xsmall-title'
]

Block.propTypes = {
  onMarkdownTargetClick: PropTypes.func,
  item: PropTypes.shape({
    anchorpoint: PropTypes.string,
    title: PropTypes.string,
    layoutVariant: PropTypes.string,
    teaserText: PropTypes.string,
    teaserTitle: PropTypes.string,
    pictures: PropTypes.array,
    videos: PropTypes.array,
    preTitlePlain: PropTypes.string,
    target: PropTypes.shape({
      href: PropTypes.string,
      teaserTitle: PropTypes.teaserTitle
    }),
    dataCmMetadata: PropTypes.string
  }),
  config: PropTypes.shape({
    imagesphere: PropTypes.shape({
      quality: PropTypes.number
    }),
    brightcove: PropTypes.shape({
      account: PropTypes.number,
      player: PropTypes.shape({
        regular: PropTypes.string,
        soundless: PropTypes.string
      })
    })
  }),
  inverseColor: PropTypes.bool,
  dropCaps: PropTypes.bool,
  imageProps: PropTypes.shape({
    lazy: PropTypes.bool
  }),
  removeLastChildMargin: PropTypes.bool,
  fullCTA: PropTypes.bool,
  fetchPriority: PropTypes.string,
  additionalOnClick: PropTypes.func
}

Block.defaultProps = {
  imageProps: {
    lazy: true
  },
  item: {},
  config: {},
  removeLastChildMargin: true,
  fullCTA: false
}

export default withTheme(Block)
